/**
 * OcrReviewPanel — Deliverable C part 4.
 *
 * Slide-over that exposes per-page OCR confidence + low-confidence
 * regions. Triggered from the TenderWorkspace header button when the
 * loaded RFP has `_ocr.ocr_page_count > 0`; for digital PDFs the
 * button is hidden so the surface stays uncluttered.
 *
 * UX shape:
 *   - Left rail: one card per OCR'd page (page #, mean confidence
 *     badge, needs_review chip when applicable). Optional toggle to
 *     hide pages with confidence >= 80.
 *   - Right pane: page image (fetched via the existing
 *     /page/{p}/image endpoint with the short-lived ?token=) overlaid
 *     with red outlined rectangles around low_conf_excerpts. Caption
 *     under each rectangle shows snippet text + confidence %.
 *   - Empty states: clear for "no OCR was used" (button shouldn't even
 *     be rendered), "OCR ran but no low-confidence regions"
 *     (everything clean), and "fetch failed" (with retry).
 *
 * Data path:
 *   1. v2Api.rfp.getOcrReview(rfpId) on open → list of OcrReviewPage
 *   2. v2Api.rfp.fileToken(rfpId) once → short-lived JWT for image
 *      URLs (browser <img> can't carry an Authorization header).
 *   3. Page image URL = v2Api.rfp.pageImageUrl(rfpId, page, token).
 *
 * Coordinate system note: the bbox coords from the backend are in
 * the OCR image's pixel space (200 dpi tesseract rendering). The
 * /page/{p}/image endpoint serves at 120 dpi by default — so we
 * scale on the FE side by reading the rendered image's natural
 * dimensions and computing the ratio against the bbox's assumed
 * source dimensions. Defer the precise scale calibration to a
 * follow-up (~per-page tesseract DPI metadata isn't surfaced yet);
 * for now we approximate by reading the natural image size and
 * assuming the bbox is fraction-of-image when its values look like
 * pixels at a known DPI.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, FileSearch, Loader2, RefreshCcw, ShieldAlert, CheckCircle2,
} from "lucide-react";

import { v2Api } from "@/lib/v2Api";
import { useRfp } from "@/contexts/RfpContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";


function confidenceTone(conf) {
  // Tesseract scale 0..100. Buckets line up with the backend's
  // needs_review threshold (80) so the badge agrees with the chip.
  if (conf >= 90) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (conf >= 80) return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-rose-100 text-rose-700 border-rose-200";
}


/** Page card on the left rail. */
function PageCard({ page, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-3 rounded-md border transition ${
        active
          ? "border-blue-400 bg-blue-50"
          : "border-slate-200 hover:border-slate-300 bg-white"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-800">
          Page {page.page}
        </span>
        <Badge className={`${confidenceTone(page.mean_confidence)} text-[11px] border`}>
          {page.mean_confidence.toFixed(1)}%
        </Badge>
      </div>
      {page.needs_review && (
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-rose-700">
          <ShieldAlert className="w-3 h-3" />
          Needs review
        </div>
      )}
      {Array.isArray(page.low_conf_excerpts) && page.low_conf_excerpts.length > 0 && (
        <p className="mt-1.5 text-[11px] text-slate-500">
          {page.low_conf_excerpts.length} low-confidence span
          {page.low_conf_excerpts.length === 1 ? "" : "s"}
        </p>
      )}
    </button>
  );
}


/** Right pane: page image + bbox overlays + excerpt list. */
function PageView({ rfpId, page, token, imageDpi }) {
  const [imgDims, setImgDims] = useState({ width: 0, height: 0 });
  const [imgError, setImgError] = useState(null);

  if (!page) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-400">
        Pick a page on the left to inspect.
      </div>
    );
  }

  const url = v2Api.rfp.pageImageUrl(rfpId, page.page, token);
  const excerpts = Array.isArray(page.low_conf_excerpts)
    ? page.low_conf_excerpts
    : [];

  // Bbox coords come from pytesseract — pixel values in a raster of
  // the page at ``page.source_dpi`` (typically 200). The page image
  // endpoint serves at ``imageDpi`` (typically 120). Convert by the
  // DPI ratio FIRST so the bbox lives in the image's native pixel
  // space, THEN scale by displayed-vs-natural to handle CSS sizing.
  // Without this the overlays land ~40% off (200 / 120 = 1.67x).
  const sourceDpi = page.source_dpi || 200;
  const dpiRatio = imageDpi ? imageDpi / sourceDpi : 1;

  const renderOverlay = (bbox, displayedDims, naturalDims, idx, conf) => {
    if (!bbox || !displayedDims.width || !naturalDims.width) return null;
    const scaleX = (displayedDims.width / naturalDims.width) * dpiRatio;
    const scaleY = (displayedDims.height / naturalDims.height) * dpiRatio;
    const x = (bbox.x ?? 0) * scaleX;
    const y = (bbox.y ?? 0) * scaleY;
    const w = (bbox.w ?? 0) * scaleX;
    const h = (bbox.h ?? 0) * scaleY;
    if (w <= 0 || h <= 0) return null;
    if (x < 0 || y < 0 || x > displayedDims.width || y > displayedDims.height) return null;
    return (
      <div
        key={`bbox-${idx}`}
        className="absolute border-2 border-rose-500 bg-rose-500/10 pointer-events-none"
        style={{ left: x, top: y, width: w, height: h }}
        title={`${(conf ?? 0).toFixed(1)}%`}
      />
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 pb-2 flex items-center justify-between border-b">
        <span className="text-sm font-semibold text-slate-800">
          Page {page.page}
        </span>
        <Badge className={`${confidenceTone(page.mean_confidence)} border`}>
          mean {page.mean_confidence.toFixed(1)}%
        </Badge>
      </div>
      <div className="relative flex-1 overflow-auto bg-slate-100">
        {imgError ? (
          <div className="p-6 text-sm text-rose-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Couldn't load page image — {imgError}
          </div>
        ) : (
          <div className="relative inline-block">
            <img
              src={url}
              alt={`Page ${page.page}`}
              onLoad={(e) => {
                const img = e.currentTarget;
                setImgDims({
                  width: img.clientWidth,
                  height: img.clientHeight,
                  naturalWidth: img.naturalWidth,
                  naturalHeight: img.naturalHeight,
                });
                setImgError(null);
              }}
              onError={() => setImgError("auth or render failure")}
              className="block max-w-full"
            />
            {excerpts.map((ex, i) => renderOverlay(
              ex.bbox,
              { width: imgDims.width, height: imgDims.height },
              { width: imgDims.naturalWidth, height: imgDims.naturalHeight },
              i, ex.mean_confidence,
            ))}
          </div>
        )}
      </div>
      <div className="px-3 py-2 border-t max-h-48 overflow-y-auto">
        {excerpts.length === 0 ? (
          <p className="text-xs text-slate-500 italic flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            No low-confidence regions on this page.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {excerpts.map((ex, i) => (
              <li key={`ex-${i}`} className="text-xs flex gap-2">
                <Badge className={`${confidenceTone(ex.mean_confidence)} border shrink-0`}>
                  {(ex.mean_confidence ?? 0).toFixed(1)}%
                </Badge>
                <span className="text-slate-700 truncate">
                  {ex.text || "(no text captured)"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


export default function OcrReviewPanel({ open, onOpenChange }) {
  const { rfpId } = useRfp();
  const [pages, setPages] = useState([]);
  const [summary, setSummary] = useState({});
  const [token, setToken] = useState(null);
  const [activePageNo, setActivePageNo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);

  const loadAll = useCallback(async () => {
    if (!rfpId) return;
    setLoading(true);
    setError(null);
    try {
      const [data, tok] = await Promise.all([
        v2Api.rfp.getOcrReview(rfpId, { needsReviewOnly }),
        v2Api.rfp.fileToken(rfpId),
      ]);
      setPages(Array.isArray(data?.pages) ? data.pages : []);
      setSummary({
        parser_used: data?.parser_used,
        pages_total: data?.pages_total,
        ocr_pages_total: data?.ocr_pages_total ?? 0,
        needs_review_count: data?.needs_review_count ?? 0,
        overall_mean_confidence: data?.overall_mean_confidence ?? 0,
        image_dpi: data?.image_dpi ?? 120,
      });
      setToken(tok);
      // Default to first needs-review page; fall back to first page.
      const firstReview = (data?.pages || []).find((p) => p.needs_review);
      const firstAny = (data?.pages || [])[0];
      setActivePageNo((firstReview || firstAny)?.page ?? null);
    } catch (err) {
      const detail = err?.response?.data?.detail ?? err?.message ?? "OCR review failed";
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [rfpId, needsReviewOnly]);

  useEffect(() => {
    if (open) loadAll();
  }, [open, loadAll]);

  const activePage = useMemo(
    () => pages.find((p) => p.page === activePageNo) || null,
    [pages, activePageNo],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl flex flex-col p-0">
        <SheetHeader className="px-4 pt-4 pb-3 border-b">
          <SheetTitle className="text-base font-semibold flex items-center gap-2">
            <FileSearch className="w-4 h-4 text-blue-500" />
            OCR review
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-500">
            {summary.parser_used || "—"}
            {" · "}
            {summary.ocr_pages_total} OCR'd page
            {summary.ocr_pages_total === 1 ? "" : "s"}
            {" · "}
            {summary.needs_review_count} need
            {summary.needs_review_count === 1 ? "s" : ""} review
            {summary.overall_mean_confidence
              ? ` · mean ${summary.overall_mean_confidence.toFixed(1)}%`
              : ""}
          </SheetDescription>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button
              size="sm" variant="outline"
              onClick={() => setNeedsReviewOnly((v) => !v)}
              className="h-7 text-xs"
            >
              {needsReviewOnly ? "Showing: needs review only" : "Showing: all OCR'd pages"}
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={loadAll}
              disabled={loading}
              className="h-7 text-xs gap-1"
            >
              {loading
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <RefreshCcw className="w-3 h-3" />}
              Refresh
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 grid grid-cols-12 overflow-hidden">
          <div className="col-span-4 border-r overflow-y-auto p-3 space-y-2 bg-slate-50/50">
            {loading && pages.length === 0 && (
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading OCR review…
              </p>
            )}
            {!loading && error && (
              <div className="text-xs text-rose-700 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> {error}
              </div>
            )}
            {!loading && !error && pages.length === 0 && (
              <p className="text-xs italic text-slate-400">
                {needsReviewOnly
                  ? "No pages need review — every OCR'd page is above 80%."
                  : "No OCR'd pages — this RFP was parsed cleanly without OCR."}
              </p>
            )}
            {pages.map((p) => (
              <PageCard
                key={p.page}
                page={p}
                active={p.page === activePageNo}
                onSelect={() => setActivePageNo(p.page)}
              />
            ))}
          </div>
          <div className="col-span-8 p-3 overflow-hidden flex flex-col">
            {token ? (
              <PageView
                rfpId={rfpId}
                page={activePage}
                token={token}
                imageDpi={summary.image_dpi}
              />
            ) : (
              <p className="text-xs text-slate-400 italic">
                Waiting for image token…
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
